import React, { useState, useEffect, useMemo } from "react";
import { Popper, Paper, ClickAwayListener } from '@mui/material';
import { Box } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import DateFilter from "src/mtska-frontend-app-component/components/List/customSelectionFilter/DateFilter/DateFilter";
import { InputAutocomplete, InputFilterMultiCheckbox } from 'src/mtska-frontend-app-component';
import { useSettingContextInComponent, useSettingsContext } from "src/mtska-frontend-app-scaffolding";
import FiltersPanel from "src/mtska-frontend-app-component/components/List/FiltersPanel";
import { getColumnDefinition } from 'src/mtska-frontend-app-component/components/List/list.functions.jsx'
import { useDataProvider } from "src/mtska-frontend-data-provider";
import JSONPretty from 'react-json-pretty';

const AGENT_EMPLOYMENT_FILTERS = {
    items: [
        { field: 'role', operator: 'equals', value: 'Agent' },
        { field: 'terminated_at_readable', operator: 'isEmpty', value: '_' },
    ],
};

const Filters = ({ dataProviderName, filters: defaultFilters, style }) => {
    const { t } = useTranslation();
    const { filterModel, drawable } = useSettingContextInComponent(dataProviderName, defaultFilters);
    const { settings, settingsLoaded, addFilter } = useSettingsContext(dataProviderName, defaultFilters);
    const [filterPanelVisible, setFilterPanelVisible] = useState(false);
    const { item: config } = useDataProvider('configs/config');

    const [customDateRange, setCustomDateRange] = useState();
    const [advertisingSourceName, setAdvertisingSourceName] = useState();
    const [areaValue, setAreaValue] = useState(null);
    const [shopValue, setShopValue] = useState(null);
    const [buyerAgentValue, setBuyerAgentValue] = useState(null);
    const [sellerAgentValue, setSellerAgentValue] = useState(null);
    const [statusValue, setStatusValue] = useState(null);
    const [productValue, setProductValue] = useState(null);

    const proposalStatusStaticOptions = useMemo(
        () => [
            { status: 'new', label: t('new') },
            { status: 'existing', label: t('existing') },
        ],
        [t],
    );

    const proposalProductStaticOptions = useMemo(
        () => [
            { product: 'buy', label: t('buy') },
            { product: 'rent', label: t('rent') },
            { product: 'referral_out', label: t('referral_out') },
            { product: 'penalty_fee', label: t('penalty_fee') },
        ],
        [t],
    );

    useEffect(() => {
        if (settingsLoaded === false) return;

        setCustomDateRange(filterModel?.items?.find(f => f.field === 'registered_at')?.value || null);
        setAdvertisingSourceName(filterModel?.items?.find(f => f.field === '_related_source_name')?.value || null);
        const areaItem = filterModel?.items?.find(f => f.field === '_related_area_name');
        const areaRaw = areaItem?.value;
        if (areaRaw == null || areaRaw === '') {
            setAreaValue(null);
        } else {
            const names =
                areaItem.operator === 'in' && Array.isArray(areaRaw) ? areaRaw : [areaRaw];
            const list = names.filter((n) => n != null && n !== '');
            setAreaValue(list.length ? { name: list } : null);
        }
        const shopItem = filterModel?.items?.find(f => f.field === '_related_shop_name');
        const shopRaw = shopItem?.value;
        if (shopRaw == null || shopRaw === '') {
            setShopValue(null);
        } else {
            const names =
                shopItem.operator === 'in' && Array.isArray(shopRaw) ? shopRaw : [shopRaw];
            const list = names.filter((n) => n != null && n !== '');
            setShopValue(list.length ? { name: list } : null);
        }
        const buyerItem = filterModel?.items?.find(f => f.field === '_related_buyerAgent_fullname');
        const buyerRaw = buyerItem?.value;
        if (buyerRaw == null || buyerRaw === '') {
            setBuyerAgentValue(null);
        } else {
            const names =
                buyerItem.operator === 'in' && Array.isArray(buyerRaw) ? buyerRaw : [buyerRaw];
            const list = names.filter((n) => n != null && n !== '');
            setBuyerAgentValue(list.length ? { _related_person_fullname: list } : null);
        }
        const sellerItem = filterModel?.items?.find(f => f.field === '_related_sellerAgent_fullname');
        const sellerRaw = sellerItem?.value;
        if (sellerRaw == null || sellerRaw === '') {
            setSellerAgentValue(null);
        } else {
            const names =
                sellerItem.operator === 'in' && Array.isArray(sellerRaw) ? sellerRaw : [sellerRaw];
            const list = names.filter((n) => n != null && n !== '');
            setSellerAgentValue(list.length ? { _related_person_fullname: list } : null);
        }

        const statusItem = filterModel?.items?.find(f => f.field === 'status');
        const statusRaw = statusItem?.value;
        if (statusRaw == null || statusRaw === '') {
            setStatusValue(null);
        } else {
            const names =
                statusItem.operator === 'in' && Array.isArray(statusRaw) ? statusRaw : [statusRaw];
            const list = names.filter((n) => n != null && n !== '');
            setStatusValue(list.length ? { status: list } : null);
        }

        const productItem = filterModel?.items?.find(f => f.field === 'product');
        const productRaw = productItem?.value;
        if (productRaw == null || productRaw === '') {
            setProductValue(null);
        } else {
            const names =
                productItem.operator === 'in' && Array.isArray(productRaw) ? productRaw : [productRaw];
            const list = names.filter((n) => n != null && n !== '');
            setProductValue(list.length ? { product: list } : null);
        }
    }, [filterModel, settingsLoaded])

    return (drawable &&
        <>
             {/* filterModel in Filters
            <JSONPretty data={filterModel} className="json-wrapper" style={{ overflow: 'scroll', fontSize: '10px' }}></JSONPretty> */}

            <Box className="section-filters" sx={style}>
                <div>
                    <DateFilter
                        applyValue={(updatedItem) => {
                            addFilter('registered_at', updatedItem.value, 'between')
                        }}
                        item={{
                            field: 'registered_at',
                            operator: 'between',
                            value: customDateRange,
                        }}
                        label="registered_at"
                    />
                </div>

                <InputFilterMultiCheckbox
                    dataProvider="commercials/area"
                    valueKey="name"
                    value={areaValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_area_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            console.info('names', names);
                            addFilter('_related_area_name', names, 'in');
                        }
                    }}
                    label={t('area')}
                    labelkey="name"
                    project={['name']}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="commercials/shop"
                    valueKey="name"
                    value={shopValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_shop_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            addFilter('_related_shop_name', names, 'in');
                        }
                    }}

                    label={t('shop_name')}
                    labelkey="name"
                    project={['name']}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="persons/employment"
                    valueKey="_related_person_fullname"
                    labelkey="_related_person_fullname"
                    value={buyerAgentValue}
                    filters={AGENT_EMPLOYMENT_FILTERS}
                    project={['_related_person_fullname']}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_buyerAgent_fullname', null);
                        } else {
                            const names = Array.isArray(v._related_person_fullname)
                                ? v._related_person_fullname
                                : [v._related_person_fullname];
                            addFilter('_related_buyerAgent_fullname', names, 'in');
                        }
                    }}
                    label={t('Buyer agent')}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="persons/employment"
                    valueKey="_related_person_fullname"
                    labelkey="_related_person_fullname"
                    value={sellerAgentValue}
                    filters={AGENT_EMPLOYMENT_FILTERS}
                    project={['_related_person_fullname']}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_sellerAgent_fullname', null);
                        } else {
                            const names = Array.isArray(v._related_person_fullname)
                                ? v._related_person_fullname
                                : [v._related_person_fullname];
                            addFilter('_related_sellerAgent_fullname', names, 'in');
                        }
                    }}
                    label={t('Seller agent')}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputAutocomplete
                    type="autocomplete"
                    dataProvider="advertisings/source/autocomplete"
                    label={t('source')}
                    searchkey={'name'}
                    labelkey={'name'}
                    project={['name']}
                    value={advertisingSourceName ? { name: advertisingSourceName } : null}
                    onChange={(v) => {
                        addFilter('_related_source_name', v?.name);
                    }}
                    style={{ flex: '1' }}
                    size={'small'}
                />

                <InputFilterMultiCheckbox
                    staticOptions={proposalStatusStaticOptions}
                    valueKey="status"
                    labelkey="label"
                    value={statusValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('status', null);
                        } else {
                            const names = Array.isArray(v.status) ? v.status : [v.status];
                            addFilter('status', names, 'in');
                        }
                    }}
                    label={t('status')}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    staticOptions={proposalProductStaticOptions}
                    valueKey="product"
                    labelkey="label"
                    value={productValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('product', null);
                        } else {
                            const names = Array.isArray(v.product) ? v.product : [v.product];
                            addFilter('product', names, 'in');
                        }
                    }}
                    label={t('product')}
                    style={{ flex: '1' }}
                    size="small"
                />

                {/* per ora disabilitiamo il bottone di apertura dei filtri
                <Button onClick={(e) => { e.stopPropagation(); setFilterPanelVisible(!filterPanelVisible)}} icon="faEllipsisVertical"></Button> 
                */}
            </Box>

            <ClickAwayListener onClickAway={() => setFilterPanelVisible(false)}>
                <Popper open={filterPanelVisible}>
                    <Paper>
                        <FiltersPanel dataProviderName={dataProviderName} columns={getColumnDefinition(settings, config, dataProviderName, t)} />
                    </Paper>
                </Popper>
            </ClickAwayListener>
        </>
    )
}

export default Filters;